/**
 * Web Logging Types
 *
 * Self-contained type definitions for the web logging system.
 * Mirrors backend LogLevel enum with identical numeric values but
 * has no dependency on @padcanvas/shared (teacher app doesn't use it).
 *
 * Uses const objects instead of enums for compatibility with
 * erasableSyntaxOnly: true in teacher/admin tsconfigs.
 */

/** Log levels - identical numeric values to backend LogLevel enum */
export const LogLevel = {
  ERROR: 1,
  WARN: 2,
  INFO1: 3,
  INFO2: 4,
  INFO3: 5,
  DEBUG: 6,
} as const;
export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

export const LogLevelName: Record<LogLevel, string> = {
  [LogLevel.ERROR]: 'Error',
  [LogLevel.WARN]: 'Warning',
  [LogLevel.INFO1]: 'Info',
  [LogLevel.INFO2]: 'Info 2',
  [LogLevel.INFO3]: 'Info 3',
  [LogLevel.DEBUG]: 'Debug',
};

/** Log level options for use in dropdowns / selectors */
export const LOG_LEVEL_OPTIONS: { value: LogLevel; label: string }[] = [
  { value: LogLevel.ERROR, label: 'Error' },
  { value: LogLevel.WARN, label: 'Warning' },
  { value: LogLevel.INFO1, label: 'Info' },
  { value: LogLevel.INFO2, label: 'Info 2' },
  { value: LogLevel.INFO3, label: 'Info 3' },
  { value: LogLevel.DEBUG, label: 'Debug' },
];

/** Web component codes - matches backend ComponentCode 60-65 */
export const WebComponentCode = {
  WEB_UI: 60,
  WEB_API: 61,
  WEB_AUTH: 62,
  WEB_NAVIGATION: 63,
  WEB_CANVAS: 64,
  WEB_STATE: 65,
  WEB_PERF: 66,
} as const;
export type WebComponentCode = (typeof WebComponentCode)[keyof typeof WebComponentCode];

export const WebComponentName: Record<WebComponentCode, string> = {
  [WebComponentCode.WEB_UI]: 'Web UI',
  [WebComponentCode.WEB_API]: 'Web API',
  [WebComponentCode.WEB_AUTH]: 'Web Auth',
  [WebComponentCode.WEB_NAVIGATION]: 'Web Navigation',
  [WebComponentCode.WEB_CANVAS]: 'Web Canvas',
  [WebComponentCode.WEB_STATE]: 'Web State',
  [WebComponentCode.WEB_PERF]: 'Web Perf',
};

/** Available web tracing backends */
export type WebTracingBackend = 'web-console' | 'web-server' | 'web-popup';

/** Per-provider configuration */
export interface WebTracingProviderConfig {
  enabled: boolean;
  logLevel: LogLevel;
}

/** Full web tracing config (fetched from backend or defaulted) */
export interface WebTracingConfig {
  providers: Partial<Record<WebTracingBackend, WebTracingProviderConfig>>;
  componentLogLevels: Partial<Record<number, LogLevel>>;
}

/** Source identifier for which app produced the log */
export type WebAppSource = 'web-student' | 'web-teacher' | 'web-admin';

/** A single structured log entry */
export interface WebLogEntry {
  sessionId: string;
  level: LogLevel;
  levelName: string;
  component: WebComponentCode;
  componentName: string;
  operation?: string;
  message: string;
  data?: Record<string, unknown>;
  error?: WebErrorDetail;
  source: WebAppSource;
  timestamp: string;
  url?: string;
}

/** Structured error detail for log entries */
export interface WebErrorDetail {
  name: string;
  message: string;
  stack?: string;
  detailCode?: number;
  context?: Record<string, unknown>;
}

/** Provider interface - all providers implement this */
export interface WebLogProvider {
  name: WebTracingBackend;
  log(entry: WebLogEntry): void;
  flush?(): Promise<void>;
  destroy?(): void;
}
